import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ShieldAlert,
  Skull,
  FileWarning,
  Lock,
  Scale,
  Shield,
  TrendingDown,
  BarChart3,
  AlertTriangle,
  MoreHorizontal,
  FolderOpen,
  Download,
  TrendingUp,
  Eye,
  Bug,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import ScoreGaugePanel from "@/components/ruby-ai-v2/dashboard/ScoreGaugePanel";
import DataPanel from "@/components/ruby-ai-v2/dashboard/DataPanel";
import ActionPlanIssuesPanel from "@/components/ruby-ai-v2/dashboard/ActionPlanIssuesPanel";
import AskRubyAIVulnerabilityWidget from "@/components/ruby-ai-v2/dashboard/AskRubyAIVulnerabilityWidget";

const tabs = [
  { id: "overview", label: "Overview", icon: ShieldAlert },
  { id: "threats", label: "Threats", icon: Skull },
  { id: "content", label: "Content", icon: FileWarning },
  { id: "security", label: "Security", icon: Lock },
  { id: "compliance", label: "Compliance", icon: Scale },
  { id: "mitigation", label: "Mitigation", icon: Shield },
] as const;

type TabId = (typeof tabs)[number]["id"];

const heroDataByTab: Record<TabId, {
  score: number;
  scoreChange: number;
  badge: string;
  title: string;
  centerLabel: string;
  subtitle: string;
  metrics: { label: string; value: string; change?: string; changeDirection?: "up" | "down" | "neutral" }[];
}> = {
  overview: {
    score: 71, scoreChange: -3,
    badge: "AI Vulnerability", title: "AI Vulnerability Score", centerLabel: "RISK LEVEL",
    subtitle: "Moderate risk detected. Content authenticity and data exposure need attention.",
    metrics: [
      { label: "Threat Level", value: "Moderate", changeDirection: "neutral" },
      { label: "Exposure Points", value: "8", changeDirection: "down" },
      { label: "Content Risk", value: "62%", changeDirection: "down" },
      { label: "Compliance", value: "74%", changeDirection: "up" },
    ],
  },
  threats: {
    score: 58, scoreChange: -5,
    badge: "Threat Landscape", title: "Threat Assessment Score", centerLabel: "THREATS",
    subtitle: "New AI-powered threats detected targeting your digital presence.",
    metrics: [
      { label: "Active Threats", value: "4", changeDirection: "down" },
      { label: "Risk Vectors", value: "12", changeDirection: "down" },
      { label: "Severity", value: "Medium", changeDirection: "neutral" },
      { label: "New This Month", value: "2", changeDirection: "down" },
    ],
  },
  content: {
    score: 62, scoreChange: 4,
    badge: "Content Auth", title: "Content Authenticity Score", centerLabel: "CONTENT",
    subtitle: "Content authenticity improving but deepfake detection gaps remain.",
    metrics: [
      { label: "Verified Content", value: "78%", change: "+4%", changeDirection: "up" },
      { label: "Deepfake Risk", value: "34%", change: "-6%", changeDirection: "up" },
      { label: "Scraping", value: "45/mo", change: "+12", changeDirection: "down" },
      { label: "Impersonation", value: "2 found", changeDirection: "down" },
    ],
  },
  security: {
    score: 82, scoreChange: 2,
    badge: "AI Security", title: "Security Posture Score", centerLabel: "SECURITY",
    subtitle: "Strong security foundation. API exposure needs hardening.",
    metrics: [
      { label: "Encryption", value: "95%", change: "+1%", changeDirection: "up" },
      { label: "API Security", value: "68%", change: "+4%", changeDirection: "up" },
      { label: "Access Control", value: "88%", change: "+2%", changeDirection: "up" },
      { label: "Monitoring", value: "76%", change: "+3%", changeDirection: "up" },
    ],
  },
  compliance: {
    score: 74, scoreChange: 6,
    badge: "Compliance", title: "AI Compliance Score", centerLabel: "COMPLIANCE",
    subtitle: "Compliance improving. GDPR AI provisions and AI Act prep needed.",
    metrics: [
      { label: "GDPR", value: "82%", change: "+4%", changeDirection: "up" },
      { label: "AI Act", value: "58%", change: "+8%", changeDirection: "up" },
      { label: "Data Privacy", value: "78%", change: "+3%", changeDirection: "up" },
      { label: "Transparency", value: "66%", change: "+5%", changeDirection: "up" },
    ],
  },
  mitigation: {
    score: 68, scoreChange: 8,
    badge: "Mitigation", title: "Mitigation Effectiveness", centerLabel: "PROTECTED",
    subtitle: "Mitigation strategies gaining effectiveness. 3 new controls deployed.",
    metrics: [
      { label: "Controls Active", value: "12", change: "+3", changeDirection: "up" },
      { label: "Effectiveness", value: "68%", change: "+8%", changeDirection: "up" },
      { label: "Response Time", value: "4.2h", change: "-1.3h", changeDirection: "up" },
      { label: "Coverage", value: "72%", change: "+6%", changeDirection: "up" },
    ],
  },
};

const mockVulnerabilitySummary = [
  { label: "Threat level: Moderate (was Low)", value: "⚠️", severity: "warn" },
  { label: "Content exposure points: 8 active", value: "8", severity: "warn" },
  { label: "AI-generated impersonation: 2 detected", value: "2", severity: "high" },
  { label: "Compliance gaps: 3 critical", value: "3", severity: "high" },
];

const mockRiskAssessment = [
  { area: "Content scraping", score: 45, severity: "HIGH" as const, trend: "↑ Increasing" },
  { area: "Deepfake exposure", score: 34, severity: "MED" as const, trend: "→ Stable" },
  { area: "API vulnerability", score: 32, severity: "MED" as const, trend: "↓ Improving" },
  { area: "Data leakage risk", score: 28, severity: "MED" as const, trend: "↓ Improving" },
  { area: "Brand impersonation", score: 22, severity: "LOW" as const, trend: "→ Stable" },
];

const mockCriticalFindings = [
  { title: "Unprotected API endpoint exposing user data", severity: "HIGH" as const, detail: "Immediate remediation required." },
  { title: "AI-generated content mimicking brand detected", severity: "HIGH" as const, detail: "Brand impersonation on 2 platforms." },
  { title: "No deepfake detection system in place", severity: "MED" as const, detail: "Leaves visual content unverified." },
  { title: "3 compliance gaps for upcoming AI regulations", severity: "MED" as const, detail: "EU AI Act provisions not addressed." },
  { title: "Content scraping increased 45% this month", severity: "LOW" as const, detail: "Monitor and consider rate limiting." },
];

const mockActiveThreats = [
  { threat: "Automated content scraping", type: "Data Theft", severity: "HIGH" as const, firstSeen: "12 days ago", status: "Active" },
  { threat: "Brand impersonation (social)", type: "Impersonation", severity: "HIGH" as const, firstSeen: "5 days ago", status: "Active" },
  { threat: "API brute force attempts", type: "Access", severity: "MED" as const, firstSeen: "3 days ago", status: "Monitored" },
  { threat: "Phishing with AI-generated emails", type: "Social Eng.", severity: "MED" as const, firstSeen: "8 days ago", status: "Blocked" },
];

const mockThreatVectors = [
  { label: "Web scraping bots", value: "342/day", change: "+45%" },
  { label: "API abuse attempts", value: "89/day", change: "+22%" },
  { label: "Deepfake content created", value: "3 this month", change: "+2" },
  { label: "Phishing campaigns detected", value: "7 active", change: "+3" },
];

const mockThreatAlerts = [
  { title: "Sophisticated scraping bot bypassing rate limits", severity: "HIGH" as const, detail: "Uses rotating proxies and browser emulation." },
  { title: "New phishing template using AI-generated brand copy", severity: "HIGH" as const, detail: "Near-perfect brand voice replication detected." },
  { title: "Competitor using scraped product data", severity: "MED" as const, detail: "Product descriptions found on third-party site." },
];

const mockContentAuthenticity = [
  { content: "Website pages", verified: "92%", risk: "Low", lastScan: "2h ago" },
  { content: "Product images", verified: "78%", risk: "Medium", lastScan: "4h ago" },
  { content: "Social media posts", verified: "65%", risk: "Medium", lastScan: "6h ago" },
  { content: "Marketing videos", verified: "45%", risk: "High", lastScan: "1d ago" },
  { content: "Customer reviews", verified: "84%", risk: "Low", lastScan: "3h ago" },
];

const mockContentMetrics = [
  { label: "Content verified", value: "78%", change: "+4%" },
  { label: "Deepfake scans run", value: "124", change: "+32" },
  { label: "Scraped content found", value: "45 pages", change: "+12" },
  { label: "Impersonation accounts", value: "2 active", change: "+1" },
];

const mockContentAlerts = [
  { title: "AI-generated video using CEO likeness detected", severity: "HIGH" as const, detail: "Found on YouTube and Twitter." },
  { title: "Product images scraped and used on competitor site", severity: "MED" as const, detail: "14 product images identified." },
  { title: "Fake review campaign detected using AI text", severity: "MED" as const, detail: "23 reviews flagged as AI-generated." },
];

const mockSecurityPosture = [
  { component: "SSL/TLS encryption", score: 95, status: "Strong", gap: "None" },
  { component: "API authentication", score: 68, status: "Needs work", gap: "Token rotation" },
  { component: "Data encryption at rest", score: 92, status: "Strong", gap: "None" },
  { component: "Network monitoring", score: 76, status: "Good", gap: "Real-time alerts" },
  { component: "Access control (RBAC)", score: 88, status: "Strong", gap: "Audit logging" },
];

const mockSecurityMetrics = [
  { label: "Vulnerabilities patched", value: "94%", change: "+3%" },
  { label: "Avg. response time", value: "4.2h", change: "-1.3h" },
  { label: "Security incidents (30d)", value: "3", change: "-2" },
  { label: "Penetration tests passed", value: "8/10", change: "+1" },
];

const mockSecurityAlerts = [
  { title: "API endpoint /v2/users lacks rate limiting", severity: "HIGH" as const, detail: "Vulnerable to data scraping and brute force." },
  { title: "JWT token expiry set to 30 days (too long)", severity: "MED" as const, detail: "Recommend reducing to 24 hours." },
  { title: "No WAF rules for AI-specific attack patterns", severity: "MED" as const, detail: "Missing protection against prompt injection." },
];

const mockComplianceFrameworks = [
  { framework: "GDPR (AI provisions)", score: 82, status: "Partial", deadline: "Active", gaps: 2 },
  { framework: "EU AI Act", score: 58, status: "In progress", deadline: "Aug 2026", gaps: 5 },
  { framework: "CCPA/CPRA", score: 76, status: "Partial", deadline: "Active", gaps: 3 },
  { framework: "ISO 27001 (AI ext.)", score: 84, status: "Compliant", deadline: "Annual", gaps: 1 },
  { framework: "SOC 2 Type II", score: 78, status: "Partial", deadline: "Q3 2026", gaps: 2 },
];

const mockComplianceMetrics = [
  { label: "Overall compliance", value: "74%", change: "+6%" },
  { label: "Open gaps", value: "13", change: "-4" },
  { label: "Audits passed", value: "3/4", change: "+1" },
  { label: "Policy docs updated", value: "8/12", change: "+3" },
];

const mockComplianceAlerts = [
  { title: "EU AI Act transparency requirements not met", severity: "HIGH" as const, detail: "AI-generated content disclosure missing." },
  { title: "GDPR data processing records incomplete for AI", severity: "MED" as const, detail: "2 AI processing activities undocumented." },
  { title: "No AI impact assessment framework established", severity: "MED" as const, detail: "Required for high-risk AI systems." },
];

const mockMitigationControls = [
  { control: "Content monitoring system", status: "Active", effectiveness: "78%", deployed: "Q4 2025" },
  { control: "API rate limiting", status: "Active", effectiveness: "85%", deployed: "Q4 2025" },
  { control: "Brand impersonation alerts", status: "Active", effectiveness: "62%", deployed: "Q1 2026" },
  { control: "Deepfake detection (pilot)", status: "Testing", effectiveness: "45%", deployed: "Q1 2026" },
  { control: "AI content watermarking", status: "Planned", effectiveness: "—", deployed: "Q2 2026" },
];

const mockMitigationMetrics = [
  { label: "Threats blocked (30d)", value: "847", change: "+234" },
  { label: "False positive rate", value: "3.2%", change: "-0.8%" },
  { label: "Avg. containment time", value: "4.2h", change: "-1.3h" },
  { label: "Coverage of risk areas", value: "72%", change: "+6%" },
];

const mockMitigationAlerts = [
  { title: "Deepfake detection pilot showing 45% accuracy — needs tuning", severity: "MED" as const, detail: "High false negative rate on video content." },
  { title: "Brand monitoring missing coverage on TikTok", severity: "MED" as const, detail: "Emerging platform not yet monitored." },
  { title: "Content watermarking rollout delayed by 2 weeks", severity: "LOW" as const, detail: "Vendor integration pending final testing." },
];

const AIVulnerabilityDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  useEffect(() => {
    window.scrollTo(0, 0);
    const mainEl = document.querySelector("main");
    if (mainEl) mainEl.scrollTop = 0;
  }, []);

  const severityColor = (sev: "HIGH" | "MED" | "LOW") => {
    switch (sev) {
      case "HIGH":
        return { bg: "rgba(248,113,113,0.15)", text: "#F87171", border: "rgba(248,113,113,0.3)" };
      case "MED":
        return { bg: "rgba(255,154,74,0.15)", text: "#FF9A4A", border: "rgba(255,154,74,0.3)" };
      case "LOW":
        return { bg: "rgba(74,222,128,0.15)", text: "#4ADE80", border: "rgba(74,222,128,0.3)" };
    }
  };

  const statusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "strong":
      case "compliant":
      case "active":
      case "low":
        return "#4ADE80";
      case "good":
      case "partial":
      case "in progress":
      case "testing":
      case "medium":
        return "#FF9A4A";
      case "needs work":
      case "high":
      case "blocked":
        return "#F87171";
      case "planned":
      case "monitored":
        return "#8B7BF7";
      default:
        return "#9AA0B3";
    }
  };

  const renderFindingsList = (items: { title: string; severity: "HIGH" | "MED" | "LOW"; detail?: string }[]) => (
    <div className="space-y-3">
      {items.map((item, i) => {
        const colors = severityColor(item.severity);
        return (
          <div key={i} className="flex items-start justify-between gap-3 rounded-xl px-4 py-3"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
            <div className="flex items-start gap-3">
              <div className="mt-0.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: colors.text }} />
              <div>
                <div className="text-sm font-medium" style={{ color: "#E8E4FF" }}>{item.title}</div>
                {item.detail && <div className="text-xs mt-0.5" style={{ color: "#9AA0B3" }}>{item.detail}</div>}
              </div>
            </div>
            <span className="text-xs px-2 py-1 rounded-full flex-shrink-0 font-medium"
              style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
              {item.severity}
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden" style={{ background: "#08080E" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0, background: "radial-gradient(620px 420px at 88% -6%, rgba(139,92,246,.15), transparent 70%), radial-gradient(560px 460px at 102% 108%, rgba(255,122,24,.10), transparent 70%)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.4, zIndex: 0, backgroundImage: "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)", backgroundSize: "46px 46px", WebkitMaskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)", maskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)" }} />

      <div className="relative z-[1] px-6 md:px-10 lg:px-14 py-6 mx-auto" style={{ ["--accent" as string]: "#EF4444", maxWidth: "1480px" } as React.CSSProperties}>
        {/* Header */}
        <div className="flex items-center gap-3.5 mb-1.5">
          <button onClick={() => navigate("/dashboard")} aria-label="Back to dashboard" className="grid place-items-center w-[38px] h-[38px] flex-none rounded-[10px] border border-white/[0.07] bg-og01-card text-og01-text-secondary transition-colors hover:border-white/[0.12] hover:text-og01-text">
            <ArrowLeft className="w-[18px] h-[18px]" />
          </button>
          <h1 className="flex items-center gap-2.5 font-heading text-[30px] leading-none text-og01-text">
            <ShieldAlert className="w-[26px] h-[26px]" style={{ color: "var(--accent)" }} />
            AI Risk &amp; Exposure
          </h1>
          <div className="flex-1" />
          <button className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] border border-white/[0.07] bg-og01-card text-[13.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12]">
            <Download className="w-[15px] h-[15px]" />Export Full Report
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] border-none text-[13.5px] font-semibold text-white transition-all hover:brightness-110" style={{ background: "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)", boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
            <FolderOpen className="w-[15px] h-[15px]" />Saved Reports
          </button>
        </div>

        {/* Metadata row */}
        <div className="flex items-center gap-5 mb-4 pl-[52px] text-[12.5px] text-og01-text-muted">
          {[
            "Risk Level: Moderate",
            "Threats: 4 Active",
            "Last Scan: 2h ago",
          ].map((item, i) => (
            <span key={i}>{item}</span>
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-5 p-1.5 rounded-[14px] overflow-x-auto border border-white/[0.07] bg-og01-card">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-[15px] py-2.5 rounded-[9px] text-[13.5px] font-medium whitespace-nowrap transition-all hover:text-og01-text"
                style={{
                  background: isActive
                    ? "color-mix(in srgb, var(--accent) 12%, transparent)"
                    : "transparent",
                  border: isActive
                    ? "1px solid color-mix(in srgb, var(--accent) 28%, transparent)"
                    : "1px solid transparent",
                  color: isActive ? "var(--accent)" : "#9AA0B3",
                }}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Score Gauge Panel */}
        <div className="mb-6">
          <ScoreGaugePanel
            score={heroDataByTab[activeTab].score}
            scoreChange={heroDataByTab[activeTab].scoreChange}
            period="Last 30 Days"
            subtitle={heroDataByTab[activeTab].subtitle}
            metrics={heroDataByTab[activeTab].metrics}
            badge={heroDataByTab[activeTab].badge}
            title={heroDataByTab[activeTab].title}
            centerLabel={heroDataByTab[activeTab].centerLabel}
          />
        </div>

        {/* Two-column layout: Data panels + Ask Ruby */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left column — tab-switched data panels (3/5 width) */}
          <div className="lg:col-span-3 space-y-6" style={{ minHeight: "calc(100vh - 200px)" }}>
            {activeTab === "overview" && (
              <>
                <ActionPlanIssuesPanel cardId="ai-vulnerability" title="Recommended Actions" />

                {/* Vulnerability Summary */}
                <DataPanel
                  title="Vulnerability Summary"
                  icon={<TrendingDown className="w-4 h-4" />}
                  actions={
                    <button className="p-1 rounded hover:bg-white/10 transition-colors">
                      <MoreHorizontal className="w-4 h-4" style={{ color: "#9AA0B3" }} />
                    </button>
                  }
                >
                  <div className="space-y-3">
                    {mockVulnerabilitySummary.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                        <span className="text-sm" style={{ color: "#C4BDE0" }}>{item.label}</span>
                        <span className="text-sm font-bold" style={{ color: item.severity === "high" ? "#F87171" : "#FF9A4A" }}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </DataPanel>

                {/* Risk Assessment */}
                <DataPanel title="Risk Assessment" icon={<BarChart3 className="w-4 h-4" />}
                  actions={<button className="px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:bg-white/10"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(139,123,247,0.15)", color: "#9AA0B3" }}>Export</button>}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                          {["Risk Area", "Score", "Severity", "Trend"].map((h) => (
                            <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {mockRiskAssessment.map((row, i) => {
                          const colors = severityColor(row.severity);
                          return (
                            <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                              <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{row.area}</td>
                              <td className="py-2.5 px-2"><span className="font-bold" style={{ color: "#FF9A4A" }}>{row.score}</span></td>
                              <td className="py-2.5 px-2">
                                <span className="text-xs px-2 py-1 rounded-full font-medium"
                                  style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
                                  {row.severity}
                                </span>
                              </td>
                              <td className="py-2.5 px-2" style={{ color: row.trend.includes("Increasing") ? "#F87171" : row.trend.includes("Improving") ? "#4ADE80" : "#9AA0B3" }}>{row.trend}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </DataPanel>

                {/* Critical Findings */}
                <DataPanel title="Critical Findings" icon={<AlertTriangle className="w-4 h-4" />}
                  actions={<button className="px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:bg-white/10"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(139,123,247,0.15)", color: "#9AA0B3" }}>Export</button>}>
                  {renderFindingsList(mockCriticalFindings)}
                </DataPanel>
              </>
            )}

            {activeTab === "threats" && (
              <>
                <DataPanel title="Active Threats" icon={<Skull className="w-4 h-4" />}
                  actions={<button className="px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:bg-white/10"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(139,123,247,0.15)", color: "#9AA0B3" }}>Export</button>}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                          {["Threat", "Type", "Severity", "First Seen", "Status"].map((h) => (
                            <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {mockActiveThreats.map((row, i) => {
                          const colors = severityColor(row.severity);
                          return (
                            <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                              <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{row.threat}</td>
                              <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{row.type}</td>
                              <td className="py-2.5 px-2">
                                <span className="text-xs px-2 py-1 rounded-full font-medium"
                                  style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
                                  {row.severity}
                                </span>
                              </td>
                              <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{row.firstSeen}</td>
                              <td className="py-2.5 px-2">
                                <span className="text-xs font-medium" style={{ color: statusColor(row.status) }}>{row.status}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </DataPanel>

                <DataPanel title="Threat Vectors" icon={<TrendingUp className="w-4 h-4" />}
                  actions={
                    <button className="p-1 rounded hover:bg-white/10 transition-colors">
                      <MoreHorizontal className="w-4 h-4" style={{ color: "#9AA0B3" }} />
                    </button>
                  }>
                  <div className="space-y-3">
                    {mockThreatVectors.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                        <span className="text-sm" style={{ color: "#C4BDE0" }}>{item.label}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-bold" style={{ color: "#FF9A4A" }}>{item.value}</span>
                          <span className="text-xs font-medium" style={{ color: "#F87171" }}>{item.change}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </DataPanel>

                <DataPanel title="Threat Alerts" icon={<AlertTriangle className="w-4 h-4" />}>
                  {renderFindingsList(mockThreatAlerts)}
                </DataPanel>
              </>
            )}

            {activeTab === "content" && (
              <>
                <DataPanel title="Content Authenticity Status" icon={<FileWarning className="w-4 h-4" />}
                  actions={<button className="px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:bg-white/10"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(139,123,247,0.15)", color: "#9AA0B3" }}>Export</button>}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                          {["Content Type", "Verified", "Risk", "Last Scan"].map((h) => (
                            <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {mockContentAuthenticity.map((row, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                            <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{row.content}</td>
                            <td className="py-2.5 px-2 font-bold" style={{ color: "#E8E4FF" }}>{row.verified}</td>
                            <td className="py-2.5 px-2">
                              <span className="text-xs font-medium" style={{ color: statusColor(row.risk) }}>{row.risk}</span>
                            </td>
                            <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{row.lastScan}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </DataPanel>

                <DataPanel title="Content Metrics" icon={<Eye className="w-4 h-4" />}
                  actions={
                    <button className="p-1 rounded hover:bg-white/10 transition-colors">
                      <MoreHorizontal className="w-4 h-4" style={{ color: "#9AA0B3" }} />
                    </button>
                  }>
                  <div className="space-y-3">
                    {mockContentMetrics.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                        <span className="text-sm" style={{ color: "#C4BDE0" }}>{item.label}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-bold" style={{ color: "#FF9A4A" }}>{item.value}</span>
                          <span className="text-xs font-medium" style={{ color: "#4ADE80" }}>{item.change}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </DataPanel>

                <DataPanel title="Content Alerts" icon={<AlertTriangle className="w-4 h-4" />}>
                  {renderFindingsList(mockContentAlerts)}
                </DataPanel>
              </>
            )}

            {activeTab === "security" && (
              <>
                <DataPanel title="Security Posture Assessment" icon={<Lock className="w-4 h-4" />}
                  actions={<button className="px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:bg-white/10"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(139,123,247,0.15)", color: "#9AA0B3" }}>Export</button>}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                          {["Component", "Score", "Status", "Gap"].map((h) => (
                            <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {mockSecurityPosture.map((row, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                            <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{row.component}</td>
                            <td className="py-2.5 px-2"><span className="font-bold" style={{ color: "#FF9A4A" }}>{row.score}</span></td>
                            <td className="py-2.5 px-2">
                              <span className="text-xs font-medium" style={{ color: statusColor(row.status) }}>{row.status}</span>
                            </td>
                            <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{row.gap}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </DataPanel>

                <DataPanel title="Security Metrics" icon={<TrendingUp className="w-4 h-4" />}
                  actions={
                    <button className="p-1 rounded hover:bg-white/10 transition-colors">
                      <MoreHorizontal className="w-4 h-4" style={{ color: "#9AA0B3" }} />
                    </button>
                  }>
                  <div className="space-y-3">
                    {mockSecurityMetrics.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                        <span className="text-sm" style={{ color: "#C4BDE0" }}>{item.label}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-bold" style={{ color: "#FF9A4A" }}>{item.value}</span>
                          <span className="text-xs font-medium" style={{ color: "#4ADE80" }}>{item.change}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </DataPanel>

                <DataPanel title="Security Alerts" icon={<AlertTriangle className="w-4 h-4" />}>
                  {renderFindingsList(mockSecurityAlerts)}
                </DataPanel>
              </>
            )}

            {activeTab === "compliance" && (
              <>
                <DataPanel title="Compliance Framework Status" icon={<Scale className="w-4 h-4" />}
                  actions={<button className="px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:bg-white/10"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(139,123,247,0.15)", color: "#9AA0B3" }}>Export</button>}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                          {["Framework", "Score", "Status", "Deadline", "Gaps"].map((h) => (
                            <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {mockComplianceFrameworks.map((row, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                            <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{row.framework}</td>
                            <td className="py-2.5 px-2"><span className="font-bold" style={{ color: "#FF9A4A" }}>{row.score}</span></td>
                            <td className="py-2.5 px-2">
                              <span className="text-xs font-medium" style={{ color: statusColor(row.status) }}>{row.status}</span>
                            </td>
                            <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{row.deadline}</td>
                            <td className="py-2.5 px-2"><span className="font-bold" style={{ color: row.gaps > 3 ? "#F87171" : row.gaps > 1 ? "#FF9A4A" : "#4ADE80" }}>{row.gaps}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </DataPanel>

                <DataPanel title="Compliance Metrics" icon={<TrendingUp className="w-4 h-4" />}
                  actions={
                    <button className="p-1 rounded hover:bg-white/10 transition-colors">
                      <MoreHorizontal className="w-4 h-4" style={{ color: "#9AA0B3" }} />
                    </button>
                  }>
                  <div className="space-y-3">
                    {mockComplianceMetrics.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                        <span className="text-sm" style={{ color: "#C4BDE0" }}>{item.label}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-bold" style={{ color: "#FF9A4A" }}>{item.value}</span>
                          <span className="text-xs font-medium" style={{ color: "#4ADE80" }}>{item.change}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </DataPanel>

                <DataPanel title="Compliance Alerts" icon={<AlertTriangle className="w-4 h-4" />}>
                  {renderFindingsList(mockComplianceAlerts)}
                </DataPanel>
              </>
            )}

            {activeTab === "mitigation" && (
              <>
                <DataPanel title="Active Mitigation Controls" icon={<Shield className="w-4 h-4" />}
                  actions={<button className="px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:bg-white/10"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(139,123,247,0.15)", color: "#9AA0B3" }}>Export</button>}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                          {["Control", "Status", "Effectiveness", "Deployed"].map((h) => (
                            <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {mockMitigationControls.map((row, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                            <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{row.control}</td>
                            <td className="py-2.5 px-2">
                              <span className="text-xs font-medium" style={{ color: statusColor(row.status) }}>{row.status}</span>
                            </td>
                            <td className="py-2.5 px-2"><span className="font-bold" style={{ color: "#FF9A4A" }}>{row.effectiveness}</span></td>
                            <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{row.deployed}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </DataPanel>

                <DataPanel title="Mitigation Metrics" icon={<TrendingUp className="w-4 h-4" />}
                  actions={
                    <button className="p-1 rounded hover:bg-white/10 transition-colors">
                      <MoreHorizontal className="w-4 h-4" style={{ color: "#9AA0B3" }} />
                    </button>
                  }>
                  <div className="space-y-3">
                    {mockMitigationMetrics.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                        <span className="text-sm" style={{ color: "#C4BDE0" }}>{item.label}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-bold" style={{ color: "#FF9A4A" }}>{item.value}</span>
                          <span className="text-xs font-medium" style={{ color: "#4ADE80" }}>{item.change}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </DataPanel>

                <DataPanel title="Mitigation Alerts" icon={<AlertTriangle className="w-4 h-4" />}>
                  {renderFindingsList(mockMitigationAlerts)}
                </DataPanel>
              </>
            )}
          </div>

          {/* Right column — Ask Ruby Chat (2/5 width) */}
          <div className="lg:col-span-2 flex flex-col">
            <div className="sticky top-6 flex-1" style={{ height: "calc(100vh - 48px)" }}>
              <AskRubyAIVulnerabilityWidget />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIVulnerabilityDashboard;
